import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  CheckCircle2,
  Clock,
  Package,
  Truck,
  Home as HomeIcon,
  XCircle,
  Phone,
  MapPin,
  RefreshCw,
} from "lucide-react";

// Fix default Leaflet marker icon paths (broken in CRA builds)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STAGES = [
  { key: "pending", icon: Clock, en: "Order received", te: "ఆర్డర్ స్వీకరించబడింది" },
  { key: "confirmed", icon: CheckCircle2, en: "Confirmed", te: "నిర్ధారించబడింది" },
  { key: "packed", icon: Package, en: "Packed", te: "ప్యాక్ చేయబడింది" },
  { key: "out_for_delivery", icon: Truck, en: "Out for delivery", te: "డెలివరీకి బయలుదేరింది" },
  { key: "delivered", icon: HomeIcon, en: "Delivered", te: "డెలివరీ అయింది" },
];

export default function Track() {
  const { code } = useParams();
  const { lang } = useApp();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const { data } = await api.get(`/track/${code}`);
      setOrder(data);
      setError("");
    } catch (err) {
      setError(err.response?.status === 404 ? "Order not found" : "Failed to load order");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 15 seconds for live tracking
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (error) {
    return (
      <div data-testid="track-error" className="bg-[#fbf8f1] min-h-[70vh] grid place-items-center px-4">
        <div className="text-center">
          <XCircle size={48} className="mx-auto text-red-500" />
          <h2 className="mt-5 text-2xl font-semibold text-stone-900">{error}</h2>
          <p className="mt-2 text-stone-500 text-sm">
            Tracking code: <span className="font-mono">{code}</span>
          </p>
        </div>
      </div>
    );
  }

  if (!order) {
    return <div className="p-10 text-center text-stone-500">Loading...</div>;
  }

  const currentIdx = STAGES.findIndex((s) => s.key === order.status);
  const isCancelled = order.status === "cancelled";
  const showMap =
    order.status === "out_for_delivery" &&
    typeof order.delivery_lat === "number" &&
    typeof order.delivery_lng === "number";

  return (
    <div data-testid="track-page" className="bg-[#fbf8f1] min-h-[80vh]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
              {lang === "en" ? "Order tracking" : "ఆర్డర్ ట్రాకింగ్"}
            </div>
            <h1 className="text-3xl font-bold text-stone-900 mt-1">
              #{order.tracking_code}
            </h1>
            <p className="text-stone-600 mt-1">
              {order.customer_name} · ₹{order.total.toFixed(0)} ·{" "}
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="track-refresh"
            className="inline-flex items-center gap-2 text-sm border border-stone-300 px-3 py-2 rounded-sm hover:bg-white disabled:opacity-60"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {lang === "en" ? "Refresh" : "రిఫ్రెష్"}
          </button>
        </div>

        {/* Timeline */}
        {isCancelled ? (
          <div
            data-testid="track-cancelled"
            className="bg-red-50 border border-red-200 rounded-sm p-6 flex items-center gap-3"
          >
            <XCircle className="text-red-600" />
            <div>
              <div className="font-semibold text-red-900">
                {lang === "en" ? "Order cancelled" : "ఆర్డర్ రద్దు చేయబడింది"}
              </div>
              <div className="text-sm text-red-700">
                {lang === "en"
                  ? "Please contact us on WhatsApp for assistance."
                  : "సహాయం కోసం వాట్సాప్ లో సంప్రదించండి."}
              </div>
            </div>
          </div>
        ) : (
          <ol
            data-testid="track-timeline"
            className="relative bg-white border border-stone-200 rounded-sm p-6"
          >
            {STAGES.map((stage, i) => {
              const Icon = stage.icon;
              const done = i <= currentIdx;
              const isCurrent = i === currentIdx;
              return (
                <li
                  key={stage.key}
                  data-testid={`stage-${stage.key}`}
                  className="flex gap-4 pb-6 last:pb-0 relative"
                >
                  {i < STAGES.length - 1 && (
                    <div
                      className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${
                        done && i < currentIdx ? "bg-[#274e13]" : "bg-stone-200"
                      }`}
                    />
                  )}
                  <div
                    className={`w-10 h-10 rounded-full grid place-items-center shrink-0 z-10 ${
                      done
                        ? "bg-[#274e13] text-white"
                        : "bg-stone-100 text-stone-400 border border-stone-200"
                    } ${isCurrent ? "ring-4 ring-[#274e13]/20" : ""}`}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 pt-1.5">
                    <div
                      className={`font-semibold ${done ? "text-stone-900" : "text-stone-400"}`}
                    >
                      {lang === "en" ? stage.en : stage.te}
                    </div>
                    {isCurrent && (
                      <div className="text-xs text-[#274e13] font-semibold mt-0.5">
                        {lang === "en" ? "Current status" : "ప్రస్తుత స్థితి"}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {/* Live Map */}
        {showMap && (
          <div
            data-testid="track-map"
            className="mt-8 bg-white border border-stone-200 rounded-sm overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
                  {lang === "en" ? "Live location" : "లైవ్ లొకేషన్"}
                </div>
                <div className="text-lg font-semibold text-stone-900">
                  {lang === "en" ? "Delivery on the way" : "డెలివరీ వస్తోంది"}
                </div>
              </div>
              <div className="text-xs text-stone-500">
                {order.delivery_updated_at &&
                  `Updated ${timeAgo(order.delivery_updated_at, lang)}`}
              </div>
            </div>
            <div className="h-80">
              <MapContainer
                center={[order.delivery_lat, order.delivery_lng]}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[order.delivery_lat, order.delivery_lng]}>
                  <Popup>
                    {lang === "en" ? "Your order is here" : "మీ ఆర్డర్ ఇక్కడ ఉంది"}
                  </Popup>
                </Marker>
                <RecenterOnChange lat={order.delivery_lat} lng={order.delivery_lng} />
              </MapContainer>
            </div>
          </div>
        )}

        {/* Order details */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-stone-200 rounded-sm p-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-stone-500 font-semibold mb-2">
              {lang === "en" ? "Delivery to" : "డెలివరీ చిరునామా"}
            </div>
            <div className="flex items-start gap-2 text-stone-900">
              <MapPin size={16} className="mt-0.5 text-[#274e13] shrink-0" />
              <div>
                <div className="font-semibold">{order.customer_name}</div>
                <div className="text-sm text-stone-600">{order.customer_address}</div>
                {order.customer_phone_masked && (
                  <div className="text-sm text-stone-500 mt-1 flex items-center gap-1">
                    <Phone size={12} /> {order.customer_phone_masked}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-stone-500 font-semibold mb-2">
              {lang === "en" ? "Items" : "వస్తువులు"}
            </div>
            <ul className="space-y-1 text-sm">
              {order.items.map((it, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="text-stone-700">
                    {lang === "en" ? it.name_en : it.name_te} · {it.weight_kg}kg × {it.quantity}
                  </span>
                  <span className="font-semibold text-stone-900">₹{it.subtotal.toFixed(0)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-stone-200 mt-3 pt-3 flex justify-between font-semibold">
              <span>{lang === "en" ? "Total" : "మొత్తం"}</span>
              <span className="text-[#274e13]">₹{order.total.toFixed(0)}</span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-stone-500">
          {lang === "en"
            ? `Need help? Chat with us on WhatsApp: +${process.env.REACT_APP_WHATSAPP_NUMBER}`
            : `సహాయం కావాలా? వాట్సాప్ లో సంప్రదించండి: +${process.env.REACT_APP_WHATSAPP_NUMBER}`}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso, lang) {
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - t) / 1000);
  if (diffSec < 60) return lang === "en" ? "just now" : "ఇప్పుడే";
  const m = Math.floor(diffSec / 60);
  if (m < 60) return lang === "en" ? `${m} min ago` : `${m} ని॥ క్రితం`;
  const h = Math.floor(m / 60);
  return lang === "en" ? `${h} hr ago` : `${h} గం॥ క్రితం`;
}

function RecenterOnChange({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}
